/*
  * Copyright Mission 42 Ventures, Inc. All rights reserved.
  * Unauthorized copying, distribution, or use is prohibited.
  */

import React, { useEffect, useState } from 'react';
import {
  Link,
  Progress,
} from '@backstage/core-components';
import {
  discoveryApiRef,
  fetchApiRef,
  storageApiRef,
  useApi,
  useAnalytics,
} from '@backstage/core-plugin-api';
import { Entity } from '@backstage/catalog-model';
import {
  Box,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Typography,
  makeStyles,
} from '@material-ui/core';
import useObservable from 'react-use/esm/useObservable';
import { PrModeChips, normalizePrMode, type PrMode } from './PrModeChips';
import { fetchPrCounts } from './prCounts';
import { useHomeScope } from './HomeScopeContext';
import { useViewAs } from '../viewAs/ViewAsContext';

const PROJECT_SLUG = 'github.com/project-slug';
// Cap the repo fan-out (one REST call per repo) the same way PendingWorkCard
// caps TechDocs/Snyk probes; alphabetical so the sample is deterministic.
const REPO_CAP = 15;
const PR_DISPLAY_CAP = 7;
// PRs are freshness-sensitive; short in-memory TTL (clears on reload).
const CACHE_TTL_MS = 5 * 60 * 1000;
const CACHE = new Map<string, { ts: number; v: PullRow[] }>();

export type PullRow = {
  title: string;
  url: string;
  number: number;
  repo: string; // short repo name (no org)
  author: string;
  createdAt: string;
  draft: boolean;
  // Unanswered review requests remain (people or teams); only the /open
  // endpoint populates it (GitHub's search API doesn't carry reviewers).
  reviewPending?: boolean;
};

// Personal modes resolve via per-user GitHub searches; scope modes share one
// /open fetch over the scope's repos ('scope-review' filters it client-side).
const PERSONAL_ENDPOINTS: Partial<Record<PrMode, string>> = {
  mine: 'involving',
  authored: 'authored',
  review: 'review-requests',
};
export const isPersonalMode = (m: PrMode): boolean => m in PERSONAL_ENDPOINTS;
export const scopeReviewRows = (pulls: PullRow[]): PullRow[] =>
  pulls.filter(p => p.reviewPending === true);

// Unique repo slugs (org/repo) across the scoped entities, alphabetical,
// capped. Exported for unit tests.
export const reposInScope = (entities: Entity[], cap = REPO_CAP): string[] => {
  const slugs = new Set<string>();
  for (const e of entities) {
    const slug = e.metadata.annotations?.[PROJECT_SLUG];
    if (slug) slugs.add(slug);
  }
  return [...slugs].sort().slice(0, cap);
};

// "3d", "5h", "2w" -- compact relative age. Exported for unit tests.
export const relativeAge = (iso: string, now = Date.now()): string => {
  const ms = now - new Date(iso).getTime();
  const h = Math.floor(ms / 3_600_000);
  if (h < 1) return '<1h';
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 14) return `${d}d`;
  return `${Math.floor(d / 7)}w`;
};

const useStyles = makeStyles(theme => ({
  meta: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
  },
  draftChip: {
    height: 18,
    fontSize: '0.65rem',
    marginLeft: theme.spacing(0.75),
  },
  empty: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    paddingTop: theme.spacing(1),
  },
  countNote: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    paddingTop: theme.spacing(1),
  },
}));

/**
 * Open pull requests across the repos of the components in the current Home
 * scope. Repos derive from the `github.com/project-slug` annotation on the
 * scoped entities; the query goes through the pull-requests-reader backend,
 * which reads GitHub with the SAME App credentials the catalog uses -- so no
 * per-user GitHub OAuth (the Google -> github-org bridge already tells us who
 * the user is and which teams scope the request).
 *
 * Repo sample capped at REPO_CAP (alphabetical, surfaced when exceeded); the
 * backend caps again server-side and caches per repo for all users. 5-min
 * client cache per repo-set on top.
 */
export const ScopedPullRequestsCardBody = () => {
  const { scopedEntities } = useHomeScope();
  const { viewAsLogin } = useViewAs();
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const storage = useApi(storageApiRef).forBucket('home');
  // Mode is STORAGE-observed, not component state: the Pending-work card's
  // review row switches this card to 'review' by writing the same key, and
  // the /pull-requests page shares the persisted choice. Legacy stored
  // values ('mine'/'scope' from the pill era) are already members of PrMode.
  const modeSnap = useObservable(
    storage.observe$<string>('prCardMode'),
    storage.snapshot<string>('prCardMode'),
  );
  const mode = normalizePrMode(modeSnap?.value);
  const analytics = useAnalytics();
  const pickMode = (next: PrMode) => {
    storage.set('prCardMode', next);
    // Which PR lens users actually reach for. surface attribute comes from
    // the AnalyticsContext wrapping the Home page.
    analytics.captureEvent('switch', `pr-mode:${next}`);
  };
  const [pulls, setPulls] = useState<PullRow[] | null>(null);
  const [failed, setFailed] = useState(false);

  const repos = reposInScope(scopedEntities);
  const totalRepos = reposInScope(scopedEntities, Number.MAX_SAFE_INTEGER).length;
  const personal = isPersonalMode(mode);
  // Scope modes share one /open fetch + cache entry; 'scope-review' is a
  // client-side filter over the same rows.
  const reposKey = personal
    ? `${mode}:${viewAsLogin ?? 'self'}`
    : `open:${repos.join('|')}`;

  // scopePulls also feeds the two scope-mode count badges, so it loads
  // eagerly (not only when a scope mode is active).
  const [scopePulls, setScopePulls] = useState<PullRow[] | null>(null);
  const scopeReposKey = `open:${repos.join('|')}`;
  useEffect(() => {
    if (repos.length === 0) {
      setScopePulls([]);
      return undefined;
    }
    let cancelled = false;
    const cached = CACHE.get(scopeReposKey);
    if (cached && Date.now() - cached.ts < CACHE_TTL_MS) {
      setScopePulls(cached.v);
      return undefined;
    }
    setScopePulls(null);
    (async () => {
      try {
        const base = await discoveryApi.getBaseUrl('pull-requests-reader');
        const url = `${base}/open?${repos.map(r => `repo=${encodeURIComponent(r)}`).join('&')}`;
        const res = await fetchApi.fetch(url);
        if (!res.ok) throw new Error(String(res.status));
        const body = (await res.json()) as { pulls: PullRow[] };
        if (cancelled) return;
        CACHE.set(scopeReposKey, { ts: Date.now(), v: body.pulls });
        setScopePulls(body.pulls);
      } catch {
        if (cancelled) return;
        setScopePulls([]);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeReposKey]);

  // Personal-mode rows.
  useEffect(() => {
    if (!personal) return undefined;
    let cancelled = false;
    setPulls(null);
    setFailed(false);
    const cached = CACHE.get(reposKey);
    if (cached && Date.now() - cached.ts < CACHE_TTL_MS) {
      setPulls(cached.v);
      return undefined;
    }
    (async () => {
      try {
        const base = await discoveryApi.getBaseUrl('pull-requests-reader');
        const url = `${base}/${PERSONAL_ENDPOINTS[mode]}${
          viewAsLogin ? `?as=${encodeURIComponent(viewAsLogin)}` : ''
        }`;
        const res = await fetchApi.fetch(url);
        if (!res.ok) throw new Error(String(res.status));
        const body = (await res.json()) as { pulls: PullRow[] };
        if (cancelled) return;
        CACHE.set(reposKey, { ts: Date.now(), v: body.pulls });
        setPulls(body.pulls);
      } catch {
        if (cancelled) return;
        setFailed(true);
        setPulls([]);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reposKey, mode]);

  // Scope modes derive their rows from the shared /open data.
  const activePulls: PullRow[] | null = personal
    ? pulls
    : scopePulls === null
    ? null
    : mode === 'scope-review'
    ? scopeReviewRows(scopePulls)
    : scopePulls;

  // Count badges: the three personal counts come from one /counts round-trip
  // (shared client cache with PendingWorkCard); scope counts fall out of the
  // eager /open fetch above.
  const [personalCounts, setPersonalCounts] = useState<{
    involves?: number;
    authored?: number;
    reviewRequested?: number;
  }>({});
  useEffect(() => {
    let cancelled = false;
    setPersonalCounts({});
    fetchPrCounts(discoveryApi, fetchApi, viewAsLogin)
      .then(v => { if (!cancelled) setPersonalCounts(v); })
      .catch(() => { /* badges hide; the modes stay usable */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewAsLogin]);
  const counts: Partial<Record<PrMode, number | null>> = {
    mine: personalCounts.involves ?? null,
    authored: personalCounts.authored ?? null,
    review: personalCounts.reviewRequested ?? null,
    scope: scopePulls === null ? null : scopePulls.length,
    'scope-review':
      scopePulls === null ? null : scopeReviewRows(scopePulls).length,
  };

  // Card deepLink: the full explore page (same modes + persisted choice).
  // Title carries the total so "Showing 7 of 26" stops being a dead end.
  // The explore page carries the lens via ?as=, so the footer link survives
  // View As instead of stranding the 7-row teaser as the only window.
  const deepLink = activePulls && activePulls.length > 0
    ? {
        title: `View all ${activePulls.length > 99 ? '99+' : activePulls.length} open PRs`,
        link: `/pull-requests${
          viewAsLogin ? `?as=${encodeURIComponent(viewAsLogin)}` : ''
        }`,
      }
    : undefined;

  const emptyCopy: Record<PrMode, string> = {
    mine: `No open pull requests involve ${viewAsLogin ?? 'you'} right now.`,
    authored: `No open pull requests opened by ${viewAsLogin ?? 'you'}.`,
    review: `No open pull requests await ${viewAsLogin ? 'their' : 'your'} review.`,
    scope: 'No open pull requests in this scope.',
    'scope-review': 'Nothing in this scope is waiting on review.',
  };

  const renderBody = () => {
    if (activePulls === null) return <Progress />;
    if (failed && isPersonalMode(mode)) {
      return (
        <Typography className={classes.empty}>
          Couldn't load pull requests right now.
        </Typography>
      );
    }
    if (activePulls.length === 0) {
      return (
        <Typography className={classes.empty}>
          {emptyCopy[mode]}
        </Typography>
      );
    }
    const shown = activePulls.slice(0, PR_DISPLAY_CAP);
    return (
      <>
        <List dense>
          {shown.map((pr, i) => (
            <React.Fragment key={pr.url}>
              {i > 0 && <Divider component="li" light />}
              <ListItem disableGutters>
                <ListItemText
                  primary={
                    <>
                      <Link
                        to={pr.url}
                        onClick={() =>
                          analytics.captureEvent('click', 'pr-deep-link', {
                            attributes: { repo: pr.repo, mode },
                          })
                        }
                      >
                        {pr.title}
                      </Link>
                      {pr.draft && (
                        <Chip label="draft" size="small" variant="outlined" className={classes.draftChip} />
                      )}
                    </>
                  }
                  secondary={
                    <Typography component="span" className={classes.meta}>
                      {pr.repo}#{pr.number} · {pr.author} · {relativeAge(pr.createdAt)}
                    </Typography>
                  }
                />
              </ListItem>
            </React.Fragment>
          ))}
        </List>
        {!isPersonalMode(mode) && totalRepos > repos.length && (
          <Typography className={classes.countNote}>
            sampling {repos.length} of {totalRepos} repos by name
          </Typography>
        )}
      </>
    );
  };

  return (
    // id is the scroll target for PendingWorkCard's "awaiting review" row
    // (it sets prCardMode='review' and scrolls here). It used to live on the
    // static Grid wrapper that the customizable grid replaced.
    <Box id="open-pull-requests">
      <Box mb={1.5}>
        <PrModeChips mode={mode} onChange={pickMode} counts={counts} />
      </Box>
      {renderBody()}
      {deepLink && (
        <Box mt={1}>
          <Link to={deepLink.link}>{deepLink.title}</Link>
        </Box>
      )}
    </Box>
  );
};
